import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { useDialer } from '@/contexts/DialerContext';
import { useTabParam } from '@/hooks/useTabParam';
import AIAssistant from '@/components/dialer/AIAssistant';
import ColdCallTrainer from '@/components/dialer/ColdCallTrainer';
import AICampaignChat from '@/components/analytics/AICampaignChat';

/**
 * Assistant IA (/ParallelDialer/tools, also /ParallelDialer/ai-chat), W4
 * (audit B2-tools). One name everywhere: menu, title, <title> (it read
 * « Formation IA », « Outils » and « Rapport IA »). The tab is in `?tab=`
 * (assistant, campaign, trainer). Two tools moved out (audit: the coaching
 * lived in three places, the scripts in two):
 *   - the script library opens from the list's « Script » panel
 *     (ScriptPanel, « Bibliothèque de scripts »);
 *   - the AI coaching is in Paramètres, section IA (/ParallelDialer/settings?section=ai).
 * An old link to ?tab=scripts or ?tab=coaching lands on the assistant, with
 * the line below saying where they went.
 */

export const TOOLS_TABS = ['assistant', 'campaign', 'trainer'] as const;
type ToolsTab = (typeof TOOLS_TABS)[number];

export const COACHING_SETTINGS_PATH = '/ParallelDialer/settings?section=ai';
export const SCRIPTS_PATH = '/ParallelDialer';

export default function ToolsScreen() {
  const { t } = useTranslation(['admin', 'dialer']);
  const { callerData, allCalls } = useDialer();
  const [tab, setTab] = useTabParam<ToolsTab>(TOOLS_TABS, 'assistant');

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground" data-testid="tools-moved">
        {t('admin:toolsPage.moved.before')}{' '}
        <Link to={SCRIPTS_PATH} className="underline underline-offset-2 text-foreground">{t('admin:toolsPage.moved.scripts')}</Link>
        {' · '}
        <Link to={COACHING_SETTINGS_PATH} className="underline underline-offset-2 text-foreground">{t('admin:toolsPage.moved.coaching')}</Link>
      </p>
      <Tabs value={tab} onValueChange={(v) => setTab(v as ToolsTab)} className="w-full">
        <TabsList className="surface-1 border border-border p-1 h-auto mb-4">
          <TabsTrigger value="assistant" className="text-sm min-h-[44px]">{t('admin:toolsPage.tabs.assistant')}</TabsTrigger>
          <TabsTrigger value="campaign" className="text-sm min-h-[44px]">{t('admin:toolsPage.tabs.campaignChat')}</TabsTrigger>
          <TabsTrigger value="trainer" className="text-sm min-h-[44px]">{t('admin:toolsPage.tabs.trainer')}</TabsTrigger>
        </TabsList>

        <TabsContent value="assistant">
          <AIAssistant clientEmail={callerData?.client_email} />
        </TabsContent>

        <TabsContent value="campaign">
          <AICampaignChat clientEmail={String(callerData?.client_email ?? '')} calls={allCalls} />
        </TabsContent>

        <TabsContent value="trainer">
          <ColdCallTrainer />
        </TabsContent>
      </Tabs>
    </div>
  );
}
